import React, { useEffect, useState, useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppFloat from '../components/WhatsAppFloat';
import ProjectsGrid from '../components/ProjectsGrid';
import PhoneNumberField from '../components/PhoneNumberField';
import emailjs from '@emailjs/browser';
import { formatPhoneWithCountryCode, getIndianPhoneError } from '../utils/phoneUtils';
import SEO from '../components/SEO';
import './Projects.css';

const Home = () => {
    const typedInstanceRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [country, setCountry] = useState('in');
    const [countryCode, setCountryCode] = useState('+91');
    const [phoneError, setPhoneError] = useState('');
    const [alertMessage, setAlertMessage] = useState({ text: '', type: '' });
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const handlePhoneChange = (digits) => {
        setFormData(prevState => ({ ...prevState, phone: digits }));
        setPhoneError(getIndianPhoneError(digits, countryCode));
    };

    const handleCountryCodeChange = ({ countryIso, countryCode: dialCode }) => {
        setCountry(countryIso || 'in');
        setCountryCode(dialCode || '+91');
        setPhoneError(getIndianPhoneError(formData.phone, dialCode || '+91'));
    };

    const sendEmail = (e) => {
        e.preventDefault();
        console.log("sendEmail function called");
        console.log("Form data:", formData);

        // Trim all values
        const trimmedData = {
            name: formData.name.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            subject: formData.subject.trim(),
            message: formData.message.trim()
        };

        const phoneValidation = getIndianPhoneError(trimmedData.phone);

        if (!trimmedData.name || !trimmedData.email || !trimmedData.phone || !trimmedData.subject || !trimmedData.message) {
            console.log("Validation failed - missing fields");
            setPhoneError(phoneValidation);
            setAlertMessage({ text: "Please fill in all fields.", type: 'error' });
            return;
        }

        if (phoneValidation) {
            console.log("Validation failed - phone error", phoneValidation);
            setPhoneError(phoneValidation);
            setAlertMessage({ text: phoneValidation, type: 'error' });
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedData.email)) {
            console.log("Invalid email format");
            setAlertMessage({ text: "Please enter a valid email address.", type: 'error' });
            return;
        }

        setIsLoading(true);
        console.log("Sending email with data:", trimmedData);
        console.log("Service ID: service_v6l9rbv, Template ID: template_zja3scs");

        const submissionData = {
            ...trimmedData,
            phone: formatPhoneWithCountryCode(countryCode, trimmedData.phone)
        };

        emailjs.send("service_v6l9rbv", "template_zja3scs", submissionData)
            .then((response) => {
                console.log("✅ SUCCESS! Response:", response);
                setAlertMessage({ text: "Your message has been sent successfully!", type: 'success' });
                setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("❌ FAILED! Error:", error);
                let errorMsg = "Something went wrong. Please try again later.";
                if (error.text) {
                    errorMsg += " " + error.text;
                }
                setAlertMessage({ text: errorMsg, type: 'error' });
                setIsLoading(false);
            });
    };

    useEffect(() => {
        emailjs.init("GPGBbocMM4O1llkd1");
        // Force hide spinner immediately
        const spinner = document.getElementById('spinner');
        if (spinner) {
            spinner.classList.remove('show');
            spinner.style.display = 'none';
            spinner.style.opacity = '0';
            spinner.style.visibility = 'hidden';
        }
        if (window.WOW) {
            new window.WOW().init();
        }

        // CounterUp initialization
        if (window.$) {
            window.$('[data-toggle="counter-up"]').counterUp({
                delay: 10,
                time: 2000
            });

            // OwlCarousel (only in Home.jsx)
            if (window.$ && window.$.fn.owlCarousel) {
                window.$('.project-carousel').owlCarousel({
                    autoplay: true,
                    smartSpeed: 1000,
                    loop: true,
                    nav: false,
                    dots: true,
                    items: 1
                });
            }

            // Testimonial Carousel initialization (from main.js if present)
            window.$(".testimonial-carousel").owlCarousel({
                autoplay: true,
                smartSpeed: 1000,
                center: true,
                dots: false,
                loop: true,
                nav: true,
                navText: [
                    '<i class="bi bi-arrow-left"></i>',
                    '<i class="bi bi-arrow-right"></i>'
                ],
                responsive: {
                    0: {
                        items: 1
                    },
                    768: {
                        items: 2
                    }
                }
            });
        }

        // Typed.js init (only in Home.jsx) with proper cleanup
        if (window.Typed && document.getElementById('element')) {
            // Destroy previous instance if it exists
            if (typedInstanceRef.current) {
                typedInstanceRef.current.destroy();
            }

            // Create new instance and store reference
            typedInstanceRef.current = new window.Typed('#element', {
                strings: ["Unleash Your Brand's", 'Roar With WebBears'],
                typeSpeed: 40,
                backDelay: 3000,
                loop: true
            });
        }

        // Cleanup function
        return () => {
            if (typedInstanceRef.current) {
                typedInstanceRef.current.destroy();
                typedInstanceRef.current = null;
            }
        };
    }, []);

    const schema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "WebBears",
        "image": "https://webbears.com/img/images/fulllogowebbears.png",
        "@id": "https://webbears.com",
        "url": "https://webbears.com",
        "telephone": "+91-8408800193",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "Bhiwandi",
            "addressLocality": "Maharashtra",
            "addressCountry": "IN"
        }
    };

    return (
        <>
            <SEO
                title="WebBears - Premium IT Services & Website Development Company"
                description="WebBears is a top-tier website development company and digital marketing agency based in Maharashtra. We excel at eCommerce development and Custom web apps."
                keywords="website development company, ecommerce website developer, local business website, professional web design"
                url="https://webbears.com"
                schemaMarkup={schema}
            />
            <WhatsAppFloat />
            <Navbar />

            {/* Carousel Start */}
            <div className="container-fluid p-0 mb-5 wow fadeIn" data-wow-delay="0.1s">
                <div id="header-carousel" className="carousel slide carousel-fade" data-bs-ride="carousel">
                    <div className="carousel-inner">
                        <div className="carousel-item active">
                            <video autoPlay loop muted className="theme-video">
                                <source src="img/images/WebBears_header.mp4" type="video/mp4" />
                            </video>
                            <div className="carousel-caption">
                                <div className="container">
                                    <div className="row justify-content-start">
                                        <div className="head-title">
                                            <h1><span id="element"></span></h1>
                                            <div id="under-webbears">
                                                <p>Our team, like a group of bears, roars with creativity and expertise,<br />
                                                    ensuring your online
                                                    success echoes far and wide.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Carousel End */}


            {/* About Start */}
            <div className="container-xxl py-5">
                <div className="container">
                    <div className="row g-4 align-items-end mb-4">
                        <div className="col-lg-6 wow fadeInUp" data-wow-delay="0.1s">
                            <img className="img-fluid rounded" src="img/images/fulllogowebbears.png" alt="About WebBears" />
                        </div>
                        <div className="col-lg-6 wow fadeInUp" data-wow-delay="0.3s">
                            <p className="d-inline-block border rounded text-primary fw-semi-bold py-1 px-3">About Us</p>
                            <h2 className="display-5 mb-4">At WebBears</h2>
                            <p className="mb-4">We are more than just a web development company, we are a team of eight
                                passionate individuals who have come together to form a creative force to be reckoned with.
                                We're not just a team; we're a pride of WebBears, and we're here to help you "Unleash Your
                                Brand & Roar With WebBears."
                            </p>
                            <div className="border rounded p-4">
                                <nav>
                                    <div className="nav nav-tabs mb-3" id="nav-tab" role="tablist">
                                        <button className="nav-link fw-semi-bold active" id="nav-story-tab" data-bs-toggle="tab"
                                            data-bs-target="#nav-story" type="button" role="tab" aria-controls="nav-story"
                                            aria-selected="true">Story</button>
                                        <button className="nav-link fw-semi-bold" id="nav-mission-tab" data-bs-toggle="tab"
                                            data-bs-target="#nav-mission" type="button" role="tab"
                                            aria-controls="nav-mission" aria-selected="false">Mission</button>
                                        <button className="nav-link fw-semi-bold" id="nav-vision-tab" data-bs-toggle="tab"
                                            data-bs-target="#nav-vision" type="button" role="tab" aria-controls="nav-vision"
                                            aria-selected="false">Vision</button>
                                    </div>
                                </nav>
                                <div className="tab-content" id="nav-tabContent">
                                    <div className="tab-pane fade show active" id="nav-story" role="tabpanel"
                                        aria-labelledby="nav-story-tab">
                                        <p>The story of WebBears begins with a shared passion for web development and a
                                            desire to make a lasting impact in the digital world.
                                            we came together with the vision of creating a web development company that
                                            stood out from the rest.
                                            We wanted to build more than websites, we wanted to build online experiences
                                            that left a mark..</p>
                                        <p className="mb-0">As a team, we decided to call ourselves WebBears, inspired by the
                                            powerful and creative characteristics of bears in the wild.
                                            Just like these magnificent creatures, we knew that our team could roar with
                                            creativity and expertise,
                                            ensuring that the success of our clients echoed far and wide.</p>
                                    </div>
                                    <div className="tab-pane fade" id="nav-mission" role="tabpanel"
                                        aria-labelledby="nav-mission-tab">
                                        <p>At WebBears, our mission is simple yet powerful: we aim to empower businesses and
                                            individuals to thrive in the digital age. </p>
                                        <p className="mb-0">We believe that every brand, no matter how big or small, deserves a
                                            strong online presence that captivates and engages its audience.
                                            We're here to turn your digital dreams into reality.</p>
                                    </div>
                                    <div className="tab-pane fade" id="nav-vision" role="tabpanel"
                                        aria-labelledby="nav-vision-tab">
                                        <p>Our vision at WebBears is to be at the forefront of innovation in web
                                            development.
                                            We envision a world where businesses can harness the full potential of the
                                            internet to grow and prosper. </p>
                                        <p className="mb-0">We strive to be the creative force behind this transformation,
                                            providing cutting-edge web solutions that set new industry standards.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            {/* About End */}



            {/* Service Start */}
            <div className="container-xxl service py-5">
                <div className="container">
                    <div className="text-center mx-auto wow fadeInUp" data-wow-delay="0.1s" style={{ maxWidth: '600px' }}>
                        <p className="d-inline-block border rounded text-primary fw-semi-bold py-1 px-3">Our Services</p>
                        <h2 className="display-5 mb-5">Our Awesome Services</h2>
                    </div>
                    <div className="row g-4 wow fadeInUp" data-wow-delay="0.3s">
                        <div className="col-lg-4">
                            <div className="nav nav-pills d-flex justify-content-between w-100 h-100 me-4">
                                <button className="nav-link w-100 d-flex align-items-center text-start border p-4 mb-4 active"
                                    data-bs-toggle="pill" data-bs-target="#tab-pane-1" type="button">
                                    <h5 className="m-0"><i className="fa-solid fa-code text-primary me-3"></i>Web Developing</h5>
                                </button>
                                <button className="nav-link w-100 d-flex align-items-center text-start border p-4 mb-4"
                                    data-bs-toggle="pill" data-bs-target="#tab-pane-2" type="button">
                                    <h5 className="m-0"><i className="fa-solid fa-cart-shopping text-primary me-3"></i>E-Commerce
                                    </h5>
                                </button>
                                <button className="nav-link w-100 d-flex align-items-center text-start border p-4 mb-4"
                                    data-bs-toggle="pill" data-bs-target="#tab-pane-3" type="button">
                                    <h5 className="m-0"><i className="fa-solid fa-users text-primary me-3"></i>Social Media Presence
                                    </h5>
                                </button>
                                <button className="nav-link w-100 d-flex align-items-center text-start border p-4 mb-0"
                                    data-bs-toggle="pill" data-bs-target="#tab-pane-4" type="button">
                                    <h5 className="m-0"><i className="fas fa-user-check text-primary me-3"></i>IT Consulting</h5>
                                </button>
                            </div>
                        </div>
                        <div className="col-lg-8">
                            <div className="tab-content w-100">
                                <div className="tab-pane fade show active" id="tab-pane-1">
                                    <div className="row g-4">
                                        <div className="col-md-6" style={{ minHeight: '350px' }}>
                                            <div className="position-relative h-100">
                                                <img className="position-absolute rounded w-100 h-100"
                                                    src="img/images/developing.png" style={{ objectFit: 'cover' }} alt="" />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <h3 className="mb-4">Web Development</h3>
                                            <p className="mb-4">Crafting innovative websites tailored to your unique needs,
                                                ensuring a user-friendly experience and high-impact online presence.</p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Cutting-edge websites</p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Custom solutions</p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Seamless user experiences</p>
                                            <a href="/contact" className="btn btn-primary py-3 px-5 mt-3">Let's Start</a>
                                        </div>
                                    </div>
                                </div>
                                <div className="tab-pane fade" id="tab-pane-2">
                                    <div className="row g-4">
                                        <div className="col-md-6" style={{ minHeight: '350px' }}>
                                            <div className="position-relative h-100">
                                                <img className="position-absolute rounded w-100 h-100"
                                                    src="img/images/e-commerce.png" style={{ objectFit: 'cover' }} alt="" />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <h3 className="mb-4">Ecommerce</h3>
                                            <p className="mb-4">We specialize in creating profitable online stores, integrating
                                                secure payment systems, and driving sales growth for your business.</p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Online store expertise</p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Payment integration</p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Boosted sales and growth</p>
                                            <a href="/contact" className="btn btn-primary py-3 px-5 mt-3">Let's Start</a>
                                        </div>
                                    </div>
                                </div>
                                <div className="tab-pane fade" id="tab-pane-3">
                                    <div className="row g-4">
                                        <div className="col-md-6" style={{ minHeight: '350px' }}>
                                            <div className="position-relative h-100">
                                                <img className="position-absolute rounded w-100 h-100"
                                                    src="img/images/social-media.png" style={{ objectFit: 'cover' }} alt="" />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <h3 className="mb-4">Social Media Presence</h3>
                                            <p className="mb-4">Our experts enhance your brand's visibility with compelling
                                                content, expanding your reach and engaging your target audience effectively.
                                            </p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Engaging content</p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Expanded reach</p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Brand visibility</p>
                                            <a href="/contact" className="btn btn-primary py-3 px-5 mt-3">Let's Start</a>
                                        </div>
                                    </div>
                                </div>
                                <div className="tab-pane fade" id="tab-pane-4">
                                    <div className="row g-4">
                                        <div className="col-md-6" style={{ minHeight: '350px' }}>
                                            <div className="position-relative h-100">
                                                <img className="position-absolute rounded w-100 h-100"
                                                    src="img/images/consulting.png" style={{ objectFit: 'cover' }} alt="" />
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <h3 className="mb-4">IT Consulting</h3>
                                            <p className="mb-4">We provide strategic technology guidance, optimizing efficiency,
                                                and fortifying your cybersecurity measures for sustainable business growth.
                                            </p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Strategic tech guidance</p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Efficiency improvements</p>
                                            <p><i className="fa fa-check text-primary me-3"></i>Enhanced cybersecurity</p>
                                            <a href="/contact" className="btn btn-primary py-3 px-5 mt-3">Let's Start</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Service End */}



            {/* Features Start */}
            <div className="container-xxl feature py-5">
                <div className="container">
                    <div className="row g-5 align-items-center">
                        <div className="col-lg-6 wow fadeInUp" data-wow-delay="0.1s">
                            <p className="d-inline-block border rounded text-primary fw-semi-bold py-1 px-3">Why Choosing Us!
                            </p>
                            <h2 className="display-5 mb-4">Few Reasons Why People Choosing Us!</h2>
                            <p className="mb-4">With a team of eight highly skilled professionals, we bring a wealth of
                                expertise to the table.
                                From web design to development and digital marketing, we have the skills needed to turn your
                                vision into reality.
                            </p>
                        </div>
                        <div className="col-lg-6">
                            <div className="row g-4 align-items-center">
                                <div className="col-md-6">
                                    <div className="row g-4">
                                        <div className="col-12 wow fadeIn" data-wow-delay="0.3s">
                                            <div className="feature-box border rounded p-4">

                                                <h4 className="mb-3">Creativity <i
                                                    className="bi bi-patch-check-fill text-primary mb-3"></i></h4>
                                                <p className="mb-3">Just like bears in the wild, our team is known for its
                                                    creativity.
                                                    We approach each project with a fresh perspective,
                                                    ensuring that your online presence stands out in a crowded digital
                                                    landscape.</p>
                                            </div>
                                        </div>
                                        <div className="col-12 wow fadeIn" data-wow-delay="0.5s">
                                            <div className="feature-box border rounded p-4">

                                                <h4 className="mb-3">Client-Centric <i
                                                    className="bi bi-patch-check-fill text-primary mb-3"></i></h4>
                                                <p className="mb-3">Your success is our success. We work closely with you to
                                                    understand your unique goals and tailor our solutions to meet your
                                                    specific needs.
                                                    Your satisfaction is our top priority.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 wow fadeIn" data-wow-delay="0.7s">
                                    <div className="feature-box border rounded p-4">

                                        <h4 className="mb-3">Roaring Results <i
                                            className="bi bi-patch-check-fill text-primary mb-3"></i></h4>
                                        <p className="mb-3">Our tagline, "Unleash Your Brand &amp; Roar With WebBears," isn't
                                            just a catchy phrase; it's a promise. We are dedicated to delivering results
                                            that make your brand roar in the online jungle.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Features End */}

            {/* Projects Start */}
            <div className="container-xxl py-5">
                <div className="container">
                    {/* Stats Section */}
                    <div className="nums-strip wow fadeInUp" data-wow-delay="0.1s">
                        <div className="ns-item">
                            <div className="ns-num">50+</div>
                            <div className="ns-lbl">Projects Delivered</div>
                        </div>
                        <div className="ns-item">
                            <div className="ns-num">40+</div>
                            <div className="ns-lbl">Happy Clients</div>
                        </div>
                        <div className="ns-item">
                            <div className="ns-num">3x</div>
                            <div className="ns-lbl">Avg Social Growth</div>
                        </div>
                        <div className="ns-item">
                            <div className="ns-num">5★</div>
                            <div className="ns-lbl">Client Rating</div>
                        </div>
                    </div>

                    <div className="text-center mx-auto wow fadeInUp" data-wow-delay="0.1s" style={{ maxWidth: '600px', marginBottom: '18px' }}>
                        <p className="d-inline-block border rounded text-primary fw-semi-bold py-1 px-3">Our Work</p>
                        <h2 className="display-5 mb-4">We Have Completed Latest Projects</h2>
                    </div>

                    {/* Filter Tabs */}
                    <div className="tab-row wow fadeInUp" data-wow-delay="0.2s">
                        <a href="/project" className="tab-btn active"><i className="fa fa-globe"></i> Web Projects</a>
                        <a href="/project" className="tab-btn"><i className="fab fa-instagram"></i> SMM & Growth</a>
                        <a href="/project" className="tab-btn"><i className="fa fa-play-circle"></i> Video Edits</a>
                        <a href="/project" className="tab-btn">View All Projects →</a>
                    </div>

                    <ProjectsGrid />
                </div>
            </div>
            {/* Projects End */}

            {/* contactUs Start */}
            <div className="container-fluid contactUs my-5 pt-5">
                <div className="container pt-5">
                    <div className="row justify-content-center">
                        <div className="col-lg-7">
                            <div className="bg-white border rounded p-4 p-sm-5 wow fadeInUp" data-wow-delay="0.5s">
                                <div className="text-center mx-auto wow fadeInUp" data-wow-delay="0.1s"
                                    style={{ maxWidth: '600px' }}>
                                    <p className="d-inline-block border rounded text-primary fw-semi-bold py-1 px-3">Get In
                                        Touch</p>
                                    <h2 className="display-5 mb-5 contactUs-head">"Unleash Your Ideas <br /> Contact WebBears"
                                    </h2>
                                </div>

                                {/* Note: In React, form submission events like onsubmit shouldn't be inline strings 
                              We'll keep standard HTML syntax translated directly as requested, but replace 
                              action="javascript:void(0);" to onSubmit hook with e.preventDefault() if needed. 
                              For now leaving closest equivalent. */}
                                <form id="contact" onSubmit={sendEmail}>
                                    {alertMessage.text && (
                                        <div style={{
                                            backgroundColor: alertMessage.type === 'success' ? "#d4edda" : "#f8d7da",
                                            color: alertMessage.type === 'success' ? "#155724" : "#721c24",
                                            border: alertMessage.type === 'success' ? "1px solid #c3e6cb" : "1px solid #f5c6cb",
                                            padding: "10px 15px",
                                            borderRadius: "4px",
                                            marginBottom: "20px"
                                        }}>
                                            {alertMessage.text}
                                        </div>
                                    )}
                                    <div className="row g-3">
                                        <div className="col-sm-6">
                                            <div className="form-floating">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="name"
                                                    placeholder="Your Name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <label htmlFor="name">Your Name</label>
                                            </div>
                                        </div>
                                        <div className="col-sm-6">
                                            <div className="form-floating">
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="email"
                                                    placeholder="Your Email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <label htmlFor="email">Your Email</label>
                                            </div>
                                        </div>
                                        <div className="col-sm-6">
                                            <PhoneNumberField
                                                phone={formData.phone}
                                                country={country}
                                                countryCode={countryCode}
                                                onPhoneChange={handlePhoneChange}
                                                onCountryChange={handleCountryCodeChange}
                                                error={phoneError}
                                            />
                                        </div>
                                        <div className="col-sm-6">
                                            <div className="form-floating">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="subject"
                                                    placeholder="Subject"
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <label htmlFor="subject">Subject</label>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-floating">
                                                <textarea
                                                    className="form-control"
                                                    placeholder="Leave a message here"
                                                    id="message"
                                                    style={{ height: '100px' }}
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    required
                                                ></textarea>
                                                <label htmlFor="message">Message</label>
                                            </div>
                                        </div>
                                        <div className="col-12 text-center">
                                            <button
                                                className="btn btn-primary w-100 py-3"
                                                type="submit"
                                                id="form-submit"
                                                disabled={isLoading}
                                            >
                                                {isLoading ? "Sending..." : "Submit Now"}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* contactUs End */}

            <Footer />
        </>
    );
};

export default Home;
